/**
 * The checkout form and what placing an order answers. The same schemas
 * validate the form in the browser and the body on the server, so every
 * field message is written for a person.
 */
import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";
import { z } from "zod";
import { Email } from "./auth.js";
import { FieldChecks } from "./field-checks.js";
export const Country = {
    CODES: ["EE", "LV", "LT", "FI", "SE", "DE", "FR", "NL", "PL"],
    schema: z.enum(["EE", "LV", "LT", "FI", "SE", "DE", "FR", "NL", "PL"], { error: "Choose a country." }),
    name: {
        EE: "Estonia",
        LV: "Latvia",
        LT: "Lithuania",
        FI: "Finland",
        SE: "Sweden",
        DE: "Germany",
        FR: "France",
        NL: "Netherlands",
        PL: "Poland",
    },
    /** Postal codes as each country's post office writes them, after trimming and uppercasing. */
    postalCode: {
        EE: /^\d{5}$/,
        LV: /^(LV-)?\d{4}$/,
        LT: /^(LT-)?\d{5}$/,
        FI: /^\d{5}$/,
        SE: /^\d{3} ?\d{2}$/,
        DE: /^\d{5}$/,
        FR: /^\d{5}$/,
        NL: /^\d{4} ?[A-Z]{2}$/,
        PL: /^\d{2}-\d{3}$/,
    },
    /** One valid postal code per country, for the field message. */
    postalCodeExample: {
        EE: "10111",
        LV: "LV-1050",
        LT: "LT-01103",
        FI: "00100",
        SE: "111 22",
        DE: "10115",
        FR: "75001",
        NL: "1012 AB",
        PL: "00-950",
    },
};
/**
 * A phone number of any country, kept as its country code, one space and the
 * rest of its digits: `+372 51234567`. Google's numbering data, through
 * libphonenumber-js, tells which country a number belongs to and whether its
 * digits make a number there.
 */
export const PhoneNumber = {
    /** Country names in English, such as "Estonia" for EE. */
    countryNames: new Intl.DisplayNames(["en"], { type: "region" }),
    /** The ISO code of the country a number typed so far belongs to; `undefined` while its digits do not say. */
    findCountry: (typed) => {
        const typer = new AsYouType();
        typer.input(typed);
        return typer.getCountry();
    },
    /** `typed` with a space after its country code, once a whole code starts it and nothing parts the two yet. */
    spaceAfterCode: (typed) => {
        const typer = new AsYouType();
        typer.input(typed);
        const code = typer.getCallingCode();
        if (code === undefined)
            return typed;
        const trimmed = typed.trimStart();
        const rest = trimmed.slice(code.length + 1);
        return !trimmed.startsWith(`+${code}`) || rest.length === 0 || /^[\s().-]/.test(rest) ? typed : `+${code} ${rest}`;
    },
    /** What is wrong with `digits`, a number with its grouping removed; `undefined` when nothing is. */
    findProblem: (digits) => {
        if (!digits.startsWith("+"))
            return "Start with the country code, such as +372 for Estonia.";
        // The code is read the way the field reads it for its flag: a number too short to parse still has one.
        const typer = new AsYouType();
        typer.input(digits);
        const code = typer.getCallingCode();
        if (code === undefined)
            return "Check the country code after the +: no country uses it.";
        const parsed = parsePhoneNumberFromString(digits);
        if (parsed?.isValid() === true)
            return undefined;
        const isoCode = parsed?.country ?? typer.getCountry();
        const country = isoCode === undefined ? undefined : PhoneNumber.countryNames.of(isoCode);
        return country === undefined
            ? `Check the digits after +${code}: they make no phone number there.`
            : `This is not a phone number in ${country}. Check the digits after +${code}.`;
    },
    schema: z
        .string()
        .trim()
        .min(1, "Enter a phone number.")
        // Spaces, dots, dashes and brackets are how people group the digits.
        .transform((phone) => phone.replace(/[\s().-]/g, ""))
        .superRefine((digits, context) => {
        const problem = PhoneNumber.findProblem(digits);
        if (problem !== undefined)
            context.addIssue({ code: "custom", message: problem });
    })
        .transform((digits) => {
        const parsed = parsePhoneNumberFromString(digits);
        return parsed === undefined ? digits : `+${parsed.countryCallingCode} ${parsed.nationalNumber}`;
    }),
};
export const Contact = {
    schema: z.object({
        fullName: z.string().trim().min(2, "Enter your full name.").max(100, "Use at most 100 characters."),
        email: Email.schema,
        phone: PhoneNumber.schema,
    }),
    /**
     * A contact as it was stored: the phone by its shape alone, since the rules
     * a typed number meets can tighten after an order was placed.
     */
    storedSchema: z.object({ fullName: z.string(), email: Email.schema, phone: z.string() }),
};
export const ShippingAddress = {
    schema: z
        .object({
        line1: z
            .string()
            .trim()
            .min(3, "Enter the street and house number.")
            .max(120, "Use at most 120 characters.")
            // A house number alone, such as "233", names no street. Asked only of a line long enough, so a blank one gets one message.
            .refine((line) => /\p{L}/u.test(line), {
            message: "Enter the street name as well as the house number.",
            when: (payload) => payload.issues.length === 0,
        }),
        /** An apartment or floor. A blank field is absent. */
        line2: z
            .string()
            .trim()
            .max(120, "Use at most 120 characters.")
            .transform((line) => (line.length === 0 ? undefined : line))
            .optional(),
        city: z
            .string()
            .trim()
            .min(1, "Enter the city.")
            .max(80, "Use at most 80 characters.")
            .refine((city) => city.length >= 2 && /\p{L}/u.test(city), {
            message: "Enter the city's full name.",
            when: (payload) => payload.issues.length === 0,
        }),
        postalCode: z.string().trim().toUpperCase().min(1, "Enter the postal code.").max(12, "Use at most 12 characters."),
        country: Country.schema,
    })
        .superRefine((address, context) => {
        if (!Country.postalCode[address.country].test(address.postalCode)) {
            context.addIssue({
                code: "custom",
                path: ["postalCode"],
                message: `Enter a postal code like ${Country.postalCodeExample[address.country]}.`,
            });
        }
    }, { when: FieldChecks.runWhenValid("postalCode", "country") }),
    /**
     * An address as it was stored: its shape alone. The rules a typed address
     * meets can tighten after an address was saved or an order placed, and
     * reading it back must not fail on them.
     */
    storedSchema: z.object({
        line1: z.string(),
        line2: z.string().optional(),
        city: z.string(),
        postalCode: z.string(),
        country: Country.schema,
    }),
};
export const ShippingMethodCode = {
    schema: z
        .string({ error: "Choose a shipping method." })
        .trim()
        .min(1, "Choose a shipping method.")
        .max(40, "Use at most 40 characters.")
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Use lowercase letters, digits and single dashes.")
        .brand(),
};
export const ShippingOption = {
    /** The delivery window in words, such as "2 to 4 business days". */
    windowOf: (option) => {
        if (option.minDays === option.maxDays) {
            return `${option.minDays} business ${option.minDays === 1 ? "day" : "days"}`;
        }
        return `${option.minDays} to ${option.maxDays} business days`;
    },
};
export const PlaceOrderInput = {
    schema: z.strictObject({
        contact: Contact.schema,
        address: ShippingAddress.schema,
        /** Where the bill goes; absent when it goes to the shipping address. */
        billingAddress: ShippingAddress.schema.optional(),
        shippingMethod: ShippingMethodCode.schema,
        /** Saves the shipping address to the signed-in customer's account. */
        shouldSaveAddress: z.boolean().optional(),
        expectedTotalCents: z.number().int().min(0),
    }),
};
export const CheckoutForm = {
    schema: PlaceOrderInput.schema.omit({ expectedTotalCents: true }),
};
export const AddressCheck = {
    NOT_FOUND: "We could not find this address. Check the street, postal code and city.",
};
export const PaymentFailure = {
    CODES: [
        "insufficient_funds",
        "card_declined",
        "expired_card",
        "incorrect_cvc",
        "incorrect_number",
        "processing_error",
        "authentication_failed",
        "gateway_timeout",
        "gateway_error",
    ],
    schema: z.enum([
        "insufficient_funds",
        "card_declined",
        "expired_card",
        "incorrect_cvc",
        "incorrect_number",
        "processing_error",
        "authentication_failed",
        "gateway_timeout",
        "gateway_error",
    ]),
    message: {
        insufficient_funds: "Your card has insufficient funds. Try another card.",
        card_declined: "Your card was declined. Try another card or contact your bank.",
        expired_card: "Your card has expired. Check the expiry date or use another card.",
        incorrect_cvc: "The security code (CVC) is incorrect.",
        incorrect_number: "The card number is incorrect.",
        processing_error: "The card could not be processed. Try again in a moment.",
        authentication_failed: "Your bank could not confirm the payment. Try again or use another card.",
        gateway_timeout: "The payment provider did not answer. You have not been charged.",
        gateway_error: "The payment provider had a problem. You have not been charged.",
    },
};
/** How a provider's error codes become a `PaymentFailure`, the same in the browser and on the server. */
export const DeclineCodes = {
    /**
     * The failure a decline code and an error code stand for. The decline code
     * is the more specific of the two, so it is tried first; an unknown card
     * error is a plain decline.
     */
    failureOf: (declineCode, code) => {
        for (const candidate of [declineCode, code]) {
            const known = PaymentFailure.schema.safeParse(candidate);
            if (known.success)
                return known.data;
        }
        if (code === "payment_intent_authentication_failure")
            return "authentication_failed";
        return code === undefined ? "processing_error" : "card_declined";
    },
};
//# sourceMappingURL=checkout.js.map